import { createContext, useContext, useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import * as storage from '@/lib/safeStorage'
import type { Tour } from '@/types'

interface TourContextValue {
  tours: Tour[]
  selectedTour: Tour | null
  setSelectedTourId: (id: string) => void
  loading: boolean
}

const TourContext = createContext<TourContextValue | null>(null)
const STORAGE_KEY = 'tourflow-selected-tour'

export function TourProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const { activeOrg, activeMembership } = useOrg()
  const role = activeMembership?.rol_nivel

  // Manager sees all tours in the active org; crew only sees active tours
  // they're assigned to via tour_memberships (freelancer model).
  const { data: tours = [], isLoading } = useQuery({
    queryKey: ['available-tours', activeOrg?.id, user?.id, role],
    enabled: !!user && !!activeOrg,
    queryFn: async (): Promise<Tour[]> => {
      let allowedTourIds: string[] | null = null

      if (role !== 'A') {
        // Two-step: get the user's tour_memberships, filter tours by those ids.
        // Keeps RLS simple (no JOIN rewrite) and lets the list be empty when
        // no tours are assigned yet.
        const { data: memberships, error: mErr } = await supabase
          .from('tour_memberships')
          .select('tour_id')
          .eq('user_id', user!.id)
        if (mErr) throw mErr
        allowedTourIds = (memberships ?? []).map(m => m.tour_id as string)
        if (allowedTourIds.length === 0) return []
      }

      let query = supabase
        .from('tours')
        .select('*')
        .eq('org_id', activeOrg!.id)
        .order('fecha_inicio', { ascending: false })
      if (role !== 'A') {
        query = query.eq('estado', 'active').in('id', allowedTourIds!)
      }
      const { data, error } = await query
      if (error) throw error
      return data ?? []
    },
    staleTime: 5 * 60 * 1000,
  })

  const [selectedIdState, setSelectedIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    return storage.getItem(STORAGE_KEY)
  })

  // Drop the in-memory tour selection on sign out so a re-login doesn't
  // resurrect a stale choice from the previous session.
  useEffect(() => {
    if (!user) setSelectedIdState(null)
  }, [user])

  const selectedTour = useMemo<Tour | null>(() => {
    if (tours.length === 0) return null
    const fromState = tours.find(t => t.id === selectedIdState)
    if (fromState) return fromState
    return tours.find(t => t.estado === 'active') ?? tours[0]
  }, [tours, selectedIdState])

  useEffect(() => {
    if (selectedTour && selectedTour.id !== selectedIdState) {
      setSelectedIdState(selectedTour.id)
      storage.setItem(STORAGE_KEY, selectedTour.id)
    }
  }, [selectedTour, selectedIdState])

  function setSelectedTourId(id: string) {
    setSelectedIdState(id)
    storage.setItem(STORAGE_KEY, id)
  }

  return (
    <TourContext.Provider value={{ tours, selectedTour, setSelectedTourId, loading: isLoading }}>
      {children}
    </TourContext.Provider>
  )
}

export function useSelectedTour() {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error('useSelectedTour must be used inside TourProvider')
  return ctx
}
